import {Tag} from '@hconnect/common/components/Tag'
import {DateFormat} from '@hconnect/uikit'
import {CardBox, MenuButton, MenuConfig} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {H3, Icon, Button, icon} from '@hm/fundamental'
import React, {FC, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useTrackDocumentsAnalytics} from '../../hooks/useTrackDocumentsAnalytics'
import {Instruction} from '../../types/documents.types'
import {EventProcessStageTitle} from '../common/EventProcessStageTitle'

import {DocumentLink} from './DocumentLink'
import {DocumentStatus} from './DocumentStatus'

type DocumentDetailsProps = {
  plantId: string
  country: string
  document: Instruction
  onClose: () => void
  onDelete: () => void
  onEdit: (id: string) => void
}

export const DocumentDetails: FC<DocumentDetailsProps> = ({
  plantId,
  country,
  document,
  onEdit,
  onDelete,
  onClose
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone()
  const trackAnalyticsEvent = useTrackDocumentsAnalytics({plantId, country})

  const onEditClick = useCallback(() => {
    onEdit(document.id)
  }, [onEdit, document.id])

  const handleClose = useCallback(() => {
    onClose()
    trackAnalyticsEvent('userClosesInstructionDetails', {instructionId: document.id})
  }, [onClose, trackAnalyticsEvent, document.id])

  const menuConfig = useMemo<MenuConfig>(
    () => [
      {
        dataTestId: 'document-edit-button',
        label: t('documents.action.edit'),
        onClick: onEditClick,
        icon: (
          <Icon
            path={icon.mdiPencilOutline}
            size="regular"
            className="mr-2 inline-block align-middle"
          />
        )
      },
      {
        dataTestId: 'document-delete-button',
        label: t('documents.action.delete'),
        onClick: onDelete,
        icon: (
          <Icon
            path={icon.mdiDeleteOutline}
            size="regular"
            className="mr-2 inline-block align-middle"
          />
        )
      }
    ],
    [onDelete, onEditClick, t]
  )

  return (
    <CardBox data-test-id="document-details">
      <div className="mb-6 flex items-center justify-between">
        <H3>{t('documents.label.details')}</H3>

        <div className="flex items-center">
          <MenuButton
            config={menuConfig}
            menuDataTestId="document-details-more-menu"
            data-test-id="document-details-more-button"
            sx={{'& .MuiSvgIcon-root': {fontSize: '16px'}}}
          />
          <Button
            variant="tertiary"
            round
            icon={icon.mdiClose}
            iconSize="regular"
            type="button"
            className="ml-6"
            onClick={handleClose}
            data-cy="document-details-close-button"
          />
        </div>
      </div>

      <div className="mb-3 mt-7 grid grid-cols-1 gap-4 min-[600px]:grid-cols-2">
        <div className="min-[600px]:col-span-2">
          <DocumentLink document={document} noUnderline={true} gap={1} iconSize={20} />
        </div>

        <div>
          <Tag
            label={t('documents.label.processStage')}
            data-test-id="document-detail-processStage"
          >
            {!document.processStage ? (
              t('shiftEvent.action.noneOfThat')
            ) : (
              <EventProcessStageTitle plantId={plantId} stage={document.processStage} />
            )}
          </Tag>
        </div>
        <div>
          <Tag label={t('documents.label.category')} data-test-id="document-detail-category">
            {document.category ? t(`shiftEvent.category.${document.category}`) : '-'}
          </Tag>
        </div>

        <div className="min-[600px]:col-span-2">
          <Tag label={t('documents.label.valid')} data-test-id="document-detail-valid">
            <DateFormat date={document.validFrom} timezone={timezone} />
            {document.validTo && (
              <>
                {' - '}
                <DateFormat date={document.validTo} timezone={timezone} />
              </>
            )}
          </Tag>
        </div>

        <div className="min-[600px]:col-span-2">
          <Tag
            label={t('documents.label.status')}
            descriptionSx={{height: 40}}
            data-test-id="document-detail-status"
          >
            <DocumentStatus document={document} />
          </Tag>
        </div>
      </div>
    </CardBox>
  )
}
